import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Plus, Trash2, ListTodo } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

export default function MicroTaskListDialog({ open, onOpenChange, officer, currentUser }) {
  const [newTask, setNewTask] = useState('');
  const queryClient = useQueryClient();
  const isOwner = currentUser?.email === officer?.user_email;

  const { data: microTasks = [], isLoading } = useQuery({
    queryKey: ['microTasks', officer?.user_email],
    queryFn: () => base44.entities.MicroTask.filter({ user_email: officer?.user_email }),
    enabled: !!officer?.user_email && open
  });

  const createMutation = useMutation({
    mutationFn: (description) => base44.entities.MicroTask.create({
      description,
      user_email: officer.user_email,
      is_completed: false
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microTasks', officer.user_email] });
      setNewTask('');
    }
  });

  const toggleMutation = useMutation({
    mutationFn: (task) => base44.entities.MicroTask.update(task.id, {
      is_completed: !task.is_completed
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microTasks', officer.user_email] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.MicroTask.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microTasks', officer.user_email] });
    }
  });

  const handleAddTask = (e) => {
    e.preventDefault();
    if (newTask.trim()) {
      createMutation.mutate(newTask.trim());
    }
  };

  const sortedTasks = [...microTasks].sort((a, b) => {
    if (a.is_completed === b.is_completed) {
      return new Date(a.created_date) - new Date(b.created_date);
    }
    return a.is_completed ? 1 : -1;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dash-microtasklistdialog-dialog" className="bg-slate-900 text-white border-slate-800 max-w-md w-full max-h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-4 border-b border-slate-800 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-indigo-400" />
            <span>{officer?.callsign || officer?.user_email}'s Quick To-Dos</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
          {isLoading ? (
            <div className="flex justify-center items-center h-full py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
            </div>
          ) : microTasks.length === 0 ? (
            <div className="text-center text-slate-500 py-8 italic">
              No quick to-dos yet.
              {isOwner && " Add one below!"}
            </div>
          ) : (
            <div className="space-y-2">
              {sortedTasks.map((task) => (
                <div 
                  key={task.id} 
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                    task.is_completed 
                      ? 'bg-slate-900/50 border-slate-800' 
                      : 'bg-slate-800/50 border-slate-700'
                  }`}
                >
                  <Checkbox 
                    checked={task.is_completed} 
                    onCheckedChange={() => isOwner && toggleMutation.mutate(task)}
                    disabled={!isOwner}
                    className="mt-1 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 border-slate-500"
                  />
                  <div className={`flex-1 text-sm ${task.is_completed ? 'text-slate-500 line-through' : 'text-slate-200'}`}>
                    {task.description}
                  </div>
                  {isOwner && (
                    <button 
                      onClick={() => deleteMutation.mutate(task.id)}
                      className="text-slate-500 hover:text-red-400 transition-colors p-0.5"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="p-4 border-t border-slate-800 bg-slate-900 shrink-0 sm:justify-between w-full">
          {isOwner ? (
            <form onSubmit={handleAddTask} className="flex gap-2 w-full">
              <Input data-testid="dash-microtasklistdialog-input"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="Add a quick to-do..."
                className="flex-1 bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
              <Button 
                type="submit" 
                disabled={!newTask.trim() || createMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
              >
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </form>
          ) : (
            <Button data-testid="dash-close-btn" variant="secondary" onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}